import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { NICHT_LESBAR } from '../../config/ebenen';
import { Loading } from '../../administration/components';
import { istSicht, vorlagenApi, type VorlagenStand } from './vorlagenApi';
import { auswahlGruppen, beispieleMitRueckfall, istFestesBeispiel, VORLAGEN_TEXTE as T, type AuswahlEintrag } from './vorlagenTexte';
import './vorlagen.css';

// Profil-Nachbau F-20 Ergänzung 3 (BAU-VORLAGEN.md 4, V-8): die Auswahl „Vorlage einfügen“ an den Eingabefeldern der Startseite
// und der Recherche. Ein modaler Dialog mit Suche; die Pfeiltasten wählen in der Liste, Enter oder Klick übernimmt. Escape,
// „Dialog schließen“ und ein Klick daneben schließen ohne Wahl und geben den Fokus an den Auslöser zurück. Bei einer Wahl
// schließt der Dialog zuerst, dann bekommt der Aufrufer den Text (onWahl) und setzt Fokus und Markierung selbst
// (ersterPlatzhalter); die Verwendung zählt der Server im Hintergrund, ein Fehler dabei bleibt still.

export default function VorlagenAuswahl(p: { offen: boolean; feldHatText: boolean; onWahl: (v: { titel: string; text: string }) => void; onSchliessen: () => void; ausloeser?: React.RefObject<HTMLElement> }) {
  return p.offen ? <AuswahlDialog {...p} /> : null;
}

function AuswahlDialog({ feldHatText, onWahl, onSchliessen, ausloeser }: { feldHatText: boolean; onWahl: (v: { titel: string; text: string }) => void;
  onSchliessen: () => void; ausloeser?: React.RefObject<HTMLElement> }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sicht = !!user?.sicht?.aktiv;
  const dialog = useRef<HTMLDialogElement>(null);
  const suchfeld = useRef<HTMLInputElement>(null);
  const gewaehlt = useRef(false);
  const titelId = useId(), listeId = useId();
  const [stand, setStand] = useState<{ daten?: VorlagenStand; fehler?: 'laden' | 'sicht' }>({});
  const [versuch, setVersuch] = useState(0);
  const [suche, setSuche] = useState('');
  const [aktiv, setAktiv] = useState(0);

  useEffect(() => {
    const element = dialog.current;
    if (element && !element.open) element.showModal();
    suchfeld.current?.focus();
    return () => {
      if (element?.open) element.close();
      if (!gewaehlt.current) ausloeser?.current?.focus();
    };
  }, []);

  useEffect(() => {
    if (sicht) { setStand({ fehler: 'sicht' }); return; }
    const abbruch = new AbortController();
    vorlagenApi.stand(abbruch.signal).then(daten => { if (!abbruch.signal.aborted) setStand({ daten }); },
      fehler => { if (!abbruch.signal.aborted) setStand({ fehler: istSicht(fehler) ? 'sicht' : 'laden' }); });
    return () => abbruch.abort();
  }, [sicht, versuch]);

  const gruppen = useMemo(() => (stand.daten ? auswahlGruppen(stand.daten, suche) : []), [stand.daten, suche]);
  const flach = gruppen.flatMap(gruppe => gruppe.eintraege.map(eintrag => ({ gruppe: gruppe.schluessel, eintrag })));
  const optionId = (index: number) => `${listeId}-${index}`;
  useEffect(() => setAktiv(0), [suche, stand.daten]);
  useEffect(() => { document.getElementById(optionId(aktiv))?.scrollIntoView({ block: 'nearest' }); }, [aktiv]);

  const waehlen = (eintrag: AuswahlEintrag) => {
    gewaehlt.current = true;
    if (!(eintrag.art === 'beispiel' && istFestesBeispiel(eintrag.id))) void (eintrag.art === 'beispiel' ? vorlagenApi.beispielVerwendet(eintrag.id) : vorlagenApi.verwendet(eintrag.id)).catch(() => undefined);
    dialog.current?.close();
    onWahl({ titel: eintrag.titel, text: eintrag.text });
    onSchliessen();
  };
  const taste = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!flach.length) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setAktiv(index => (index + 1) % flach.length); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setAktiv(index => (index - 1 + flach.length) % flach.length); }
    else if (event.key === 'Enter') { event.preventDefault(); waehlen(flach[Math.min(aktiv, flach.length - 1)].eintrag); }
  };
  const verwalten = () => { gewaehlt.current = true; onSchliessen(); navigate('/vorlagen'); };
  // F-23 (PF-NB-hilfe-4): ohne Beispiel des Servers zählen die drei festen mit, sonst verdeckte „leer“ sie.
  const leer = !!stand.daten && !stand.daten.meine.vorlagen.length && !(stand.daten.firma?.vorlagen.length) && !beispieleMitRueckfall(stand.daten.beispiele).length;
  let index = -1;

  return <dialog ref={dialog} className="vorlagen-auswahl" aria-labelledby={titelId} aria-modal="true"
    onCancel={event => { event.preventDefault(); onSchliessen(); }}
    onClick={event => { if (event.target === event.currentTarget) onSchliessen(); }}>
    <div className="vorlagen-auswahl-rahmen">
      <div className="vorlagen-auswahl-kopf">
        <h2 id={titelId}>{T.einfuegen}</h2>
        <button type="button" className="vorlagen-knopf vorlagen-auswahl-schliessen" aria-label={T.schliessen} onClick={onSchliessen}><X size={16} aria-hidden="true" /></button>
      </div>
      <span className="vorlagen-auswahl-suche"><Search size={16} aria-hidden="true" />
        <input ref={suchfeld} type="search" role="combobox" aria-label={T.suche} placeholder={T.suche} autoComplete="off" value={suche}
          aria-expanded={flach.length > 0} aria-controls={flach.length ? listeId : undefined} aria-autocomplete="list"
          aria-activedescendant={flach.length ? optionId(aktiv) : undefined} onChange={event => setSuche(event.target.value)} onKeyDown={taste} /></span>
      {feldHatText && <p className="adm-info vorlagen-auswahl-info">{T.ersetzt}</p>}
      <div className="vorlagen-auswahl-liste">
        {stand.fehler === 'sicht' ? <p className="vorlagen-auswahl-leer">{NICHT_LESBAR}</p>
          : stand.fehler === 'laden' ? <div className="vorlagen-auswahl-leer">
            <p className="adm-error vorlagen-auswahl-fehler" role="alert">{T.ladeFehler}</p>
            <button type="button" className="vorlagen-knopf" onClick={() => { setStand({}); setVersuch(wert => wert + 1); }}>{T.erneut}</button>
          </div>
            : !stand.daten ? <Loading text={T.laden} />
              : leer ? <p className="vorlagen-auswahl-leer">{T.auswahlLeer}</p>
                : !flach.length ? <p className="vorlagen-auswahl-leer" role="status">{T.keineTreffer}</p>
                  : <div id={listeId} role="listbox" aria-label={T.einfuegen}>
                    {gruppen.map(gruppe => <div key={gruppe.schluessel} role="group" aria-labelledby={`${listeId}-${gruppe.schluessel}`}>
                      <div id={`${listeId}-${gruppe.schluessel}`} className="vorlagen-auswahl-gruppe">{gruppe.titel}</div>
                      {gruppe.eintraege.map(eintrag => {
                        const nummer = ++index;
                        return <div key={`${gruppe.schluessel}-${eintrag.art}-${eintrag.id}`} id={optionId(nummer)} role="option" aria-selected={nummer === aktiv}
                          className="vorlagen-auswahl-zeile" onMouseDown={event => event.preventDefault()} onMouseMove={() => setAktiv(nummer)}
                          onClick={() => waehlen(eintrag)}>
                          <span className="vorlagen-auswahl-titel">{eintrag.titel}</span>
                          <span className="vorlagen-auswahl-kurz">{eintrag.kurzbeschreibung}</span>
                        </div>;
                      })}
                    </div>)}
                  </div>}
      </div>
      <div className="vorlagen-auswahl-fuss">
        <button type="button" className="vorlagen-knopf vorlagen-knopf--text" onClick={verwalten}>{T.verwalten}</button>
      </div>
    </div>
  </dialog>;
}
